import React, { createContext, useState } from 'react';
import PropTypes from 'prop-types';

export const STEPS = {
  ACTIVATE: { id: 'activate', title: 'Complete Your Profile' },
  ACTIVATE_CONFIRM: {
    id: 'activate_confirm',
    title: 'Activate Your Account'
  },
  ACTIVATE_SUCCESS: {
    id: 'activate_success',
    title: 'Account Created',
    body: 'Your account is ready to go!'
  },
  SIGNUP: { id: 'signup', title: 'Sign Up' },
  LOGIN: {
    id: 'login',
    title: 'Log In'
  },
  LOGIN_SUCCESS: {
    id: 'login_success',
    title: 'Welcome Back',
    body: 'You\'re In!',
    cta: 'Continue'
  },
  CONFIRM: {
    id: 'confirm',
    title: 'Confirm Your Email'
  },
  FORGOT: { id: 'forgot', title: 'Reset Password' },
  UPDATE: { id: 'update', title: 'Update Password' },
  UPDATE_SUCCESS: {
    id: 'update_success',
    title: 'Password Updated',
    body: 'Your password has been updated.',
    cta: 'Continue'
  },
  WELCOME: { id: 'welcome', title: 'Welcome' }
};

/**
 * @typedef {Object} AuthenticationContext
 * @property {Object} currentStep - The current step of the authentication flow
 * @property {String} currentStep.id - id of the step
 * @property {String} currentStep.title - title of the step
 * @property {func} setCurrentStep - Updates the current step in the authentication flow
 * @property {func} resetFlow - Resets the step in the authentication flow to the original step on a dialog close
 * @property {Boolean} isDialogOpen - Determines if the authentication flow is open when rendered within an authentication dialog
 * @property {func} setIsDialogOpen - sets if the authentication flow is open when rendered within an authentication dialog
 */
export const AuthenticationContext = createContext();

/**
 * Custom hook for using authentication context in a component
 * @returns {AuthenticationControlContext} Authentication Control Properties
 */
export const useAuthState = () => {
  const context = React.useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error(
      'useAuthState must be used within an AuthenticationProvider'
    );
  }
  return context;
};

/**
 * AuthenticationProvider is used to provide authentication control context to AuthenticationBody or AuthenticationDialog
 * This ensures the Authentication flow can be rendered directly on the page or within a modal.
 */
const AuthenticationProvider = ({ isOpen, step, children }) => {
  const [currentStep, setCurrentStep] = useState(step);
  const [isDialogOpen, setIsDialogOpen] = useState(isOpen);

  const resetFlow = () => {
    setCurrentStep(step);
  };

  const authContext = {
    currentStep,
    setCurrentStep,
    resetFlow,
    isDialogOpen,
    setIsDialogOpen
  };

  return (
    <AuthenticationContext.Provider value={authContext}>
      {children}
    </AuthenticationContext.Provider>
  );
};

AuthenticationProvider.propTypes = {
  /**
   * Content to render inside AuthenticationControl provider tree
   */
  children: PropTypes.node.isRequired,
  /**
   * Step of the authentication flow to start with
   */
  step: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string
  }),
  /**
   * `true` to display when rendered as a dialog
   */
  isOpen: PropTypes.bool
};

AuthenticationProvider.defaultProps = {
  step: STEPS.LOGIN,
  isOpen: false
};

export default AuthenticationProvider;
